import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import {
  staffAPI,
  bookingsAPI,
  pricingAPI,
  suitesAPI,
} from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import "react-datepicker/dist/react-datepicker.css";
import "./StaffBookingForm.css";

const StaffBookingForm = ({ customerId, onSuccess, onCancel }) => {
  const [sessionType, setSessionType] = useState("infrared"); // default infrared
  const [selectedDay, setSelectedDay] = useState(new Date()); // Use current date as default
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableSuites, setAvailableSuites] = useState([]);
  const [filteredSuites, setFilteredSuites] = useState([]);
  const [selectedSuite, setSelectedSuite] = useState("");
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [addGuest, setAddGuest] = useState(false);
  const [aromatherapy, setAromatherapy] = useState(false);
  const [halotherapy, setHalotherapy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isAda, setIsAda] = useState(false);
  const [bypassSessionLimits, setBypassSessionLimits] = useState(false);
  // Massage only: book even if the slot is inside another massage's
  // turnover pad (the suite must still be free).
  const [overrideMassageTurnover, setOverrideMassageTurnover] =
    useState(false);
  // Prices for the selected day from /api/pricing (Stripe): massages,
  // drop-in (Wellness Wednesday price on a Wednesday), and add-ons.
  const [dayPricing, setDayPricing] = useState(null);
  // No credit for the session: charge the drop-in price to the saved card.
  const [chargeSavedCardForSession, setChargeSavedCardForSession] =
    useState(false);
  // Guest / halotherapy: charge the saved card (on by default).
  const [chargeSavedCardForAddOns, setChargeSavedCardForAddOns] =
    useState(true);
  // How a charge is paid: the saved card, or the POS card reader.
  const [paymentMethod, setPaymentMethod] = useState("saved_card");
  const [cardReaders, setCardReaders] = useState(null);
  const [cardReadersError, setCardReadersError] = useState("");
  const [selectedReaderId, setSelectedReaderId] = useState("");
  // The reader payment in progress: { paymentIntentId, amount, status,
  // message }. status: "waiting" | "paid" | "failed" | "timeout".
  const [readerPayment, setReaderPayment] = useState(null);
  // A paid reader payment that no booking has used yet, with the booking
  // details it was paid for. Pressing Book again reuses it.
  const [unusedReaderPayment, setUnusedReaderPayment] = useState(null);
  const readerPollRef = useRef({ active: false });
  const notificationRef = useRef(null);
  // The reader was refused as busy: offer "Clear reader" (issue #289).
  const [readerBusy, setReaderBusy] = useState(false);
  const [clearingReader, setClearingReader] = useState(false);
  // Bring a new message into view (the form can be taller than the modal).
  useEffect(() => {
    if (!(message || error)) return;
    const node = notificationRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [message, error]);
  // Credit sources the booking will use, from the backend (legacy credits
  // that are still honored and shared credits). Null until loaded or when
  // the request fails; the form then falls back to its local calculation.
  const [creditSources, setCreditSources] = useState(null);
  // creditTypeToUse drives both the dropdown selection and the body sent to
  // staffBookSession. Valid values:
  //   "auto"            — backend picks per priority order (gifted → drop-in → package → membership)
  //   "gifted-sauna"    — User.giftedSaunaCredits
  //   "gifted-redlight" — User.giftedRedLightCredits
  //   "shared-gifted"   — User.sharedGiftedCredits
  //   "promotion"       — User.wellnessWednesdayCredits
  //   "drop-in"         — User.dropInSaunaCount / dropInRedLightCount / sharedDropInCount per session type
  //   "package"         — saunaPackageSessionUsage / redLightPackageSessionUsage / sharedPackageSessionUsage
  //   "membership"      — saunaMembershipSessionUsage / redLightMembershipSessionUsage / sharedMembershipSessionUsage
  const [creditTypeToUse, setCreditTypeToUse] = useState("auto");
  const navigate = useNavigate();
  const isUnifiedShared = userProfile?.creditSystemType === "unified_shared";
  const corporateProgramStatus = userProfile?.corporateProgramStatus || null;
  const corporateProgramAppliesToSession =
    corporateProgramStatus &&
    (!corporateProgramStatus.eligibleSessionTypes?.length ||
      corporateProgramStatus.eligibleSessionTypes.includes(sessionType));
  // Staff can book massages. The backend uses the customer massage flow:
  // the therapist and room are assigned automatically, and the massage is
  // charged to the customer's saved card (Stripe price + tax).
  const isMassage = sessionType === "massage_60" || sessionType === "massage_90";
  const pricingDateIso = moment(selectedDay)
    .tz("America/Phoenix")
    .format("YYYY-MM-DD");
  useEffect(() => {
    let cancelled = false;
    pricingAPI
      .getPricing(pricingDateIso)
      .then((response) => {
        if (!cancelled) setDayPricing(response.data || null);
      })
      .catch(() => {
        if (!cancelled) setDayPricing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pricingDateIso]);
  const massagePricing = isMassage
    ? dayPricing?.massages?.[sessionType] || null
    : null;
  const formatCents = (cents) => `$${(Number(cents) / 100).toFixed(2)}`;

  useEffect(() => {
    if (isMassage || !customerId) {
      setCreditSources(null);
      return undefined;
    }
    let cancelled = false;
    staffAPI
      .getCustomerCreditSources(customerId, sessionType, pricingDateIso)
      .then((response) => {
        if (!cancelled) setCreditSources(response.data?.sources || []);
      })
      .catch(() => {
        if (!cancelled) setCreditSources(null);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, isMassage, sessionType, pricingDateIso]);
  const isWellnessWednesdayEligible =
    moment(selectedDay).tz("America/Phoenix").day() === 3 &&
    ["infrared", "redlight"].includes(sessionType);
  const getUnifiedMembershipDisplayName = () => {
    const name = String(userProfile?.sharedMembershipName || "").trim();
    if (name && name.toLowerCase() !== "none" && name !== "Shared Membership") {
      return name;
    }

    const monthlyCredits =
      userProfile?.sharedMembershipSessionUsage?.totalSessions ||
      userProfile?.sharedMembershipSessionUsage?.remaining ||
      0;

    if (monthlyCredits === 4) {
      return "Diviti Glow";
    }

    if (monthlyCredits === 8) {
      return "Diviti Radiance";
    }

    if (monthlyCredits >= 12) {
      return "Diviti Elite";
    }

    return "Flexible Session Credits Membership";
  };

  const sharedMembershipDisplayName =
    userProfile?.sharedMembershipName &&
    userProfile.sharedMembershipName !== "none" &&
    userProfile.sharedMembershipName !== "Shared Membership"
      ? userProfile.sharedMembershipName
      : getUnifiedMembershipDisplayName();
  const sharedPackageDisplayName =
    userProfile?.sharedPackageName && userProfile.sharedPackageName !== "none"
      ? userProfile.sharedPackageName
      : "Flexible Session Package Credits";

  // Credit-source render contract:
  //   Gifted (per-type + shared) and Wellness Wednesday credits are ALWAYS
  //   surfaced when populated, regardless of creditSystemType. This mirrors
  //   BookSession.js (customer-side) semantics — both legacy per-type gifted
  //   buckets (giftedSaunaCredits / giftedRedLightCredits) AND the unified
  //   sharedGiftedCredits bucket can co-exist on a single User document, and
  //   the backend SessionSourceManager.getAvailableSources() treats them as
  //   additive sources. Bug fix per BUG-STAFF-BOOKING-GIFTED-CREDITS-001.
  //
  //   Membership / package / drop-in remain creditSystemType-segregated
  //   because those buckets are genuinely mutually exclusive in the data
  //   model — a customer is either on the unified-shared rails OR the legacy
  //   per-type rails for those source types.
  const sessionTypeIsInfrared = sessionType === "infrared";

  // Each gifted option's `type` value is sent as bookingData.creditTypeToUse
  // when staff explicitly picks a source. Per-type gifted (giftedSauna/
  // giftedRedLight) and shared gifted (sharedGifted) are surfaced as
  // distinct options because they are distinct buckets on the User
  // document — a customer can hold both simultaneously, and a staff user
  // booking against one should not silently consume the other. Backend
  // staffBookSession will need to honour these disambiguated type values
  // (BUG-STAFF-BOOKING-GIFTED-CREDITS-001 backend half — escalated to
  // Simone in backend-engineer.md).
  const giftedPerTypeOption = {
    type: sessionTypeIsInfrared ? "gifted-sauna" : "gifted-redlight",
    available: sessionTypeIsInfrared
      ? userProfile?.giftedSaunaCredits?.remaining || 0
      : userProfile?.giftedRedLightCredits?.remaining || 0,
    label: sessionTypeIsInfrared
      ? "Gifted Sauna Credits"
      : "Gifted Red Light Credits",
    description: sessionTypeIsInfrared
      ? "Gifted Sauna Credits"
      : "Gifted Red Light Credits",
    className: "gifted",
  };

  const sharedGiftedOption = {
    type: "shared-gifted",
    available: userProfile?.sharedGiftedCredits?.remaining || 0,
    label: isUnifiedShared
      ? "Gifted Credits"
      : "Flexible Gifted Credits",
    description: isUnifiedShared
      ? "Flexible Session Gifted Credits"
      : "Flexible Gifted Credits",
    className: "gifted",
  };

  const wellnessWednesdayOption = {
    type: "promotion",
    available: isWellnessWednesdayEligible
      ? userProfile?.wellnessWednesdayCredits?.remaining || 0
      : 0,
    label: "Wellness Wednesday Credits",
    description: "Wellness Wednesday Session",
    className: "drop-in",
  };

  const unifiedSharedOptions = [
    giftedPerTypeOption,
    sharedGiftedOption,
    wellnessWednesdayOption,
    {
      type: "drop-in",
      available: userProfile?.sharedDropInCount?.remaining || 0,
      label: "Drop-in Credits",
      description: "Drop-In Session Credits",
      className: "drop-in",
    },
    {
      type: "package",
      available: userProfile?.sharedPackageSessionUsage?.remaining || 0,
      label: "Package Credits",
      description: sharedPackageDisplayName,
      className: "package",
    },
    {
      type: "membership",
      available: userProfile?.sharedMembershipSessionUsage?.remaining || 0,
      label: "Membership Credits",
      description: sharedMembershipDisplayName,
      className: "membership",
    },
  ];

  const legacyInfraredOptions = [
    giftedPerTypeOption,
    sharedGiftedOption,
    wellnessWednesdayOption,
    {
      type: "drop-in",
      available: userProfile?.dropInSaunaCount?.remaining || 0,
      label: "Drop-in Credits",
      description: "Drop-in Sauna Credits",
      className: "drop-in",
    },
    {
      type: "package",
      available: userProfile?.saunaPackageSessionUsage?.remaining || 0,
      label: "Package Credits",
      description: "Package Sauna Credits",
      className: "package",
    },
    {
      type: "membership",
      available: userProfile?.saunaMembershipSessionUsage?.remaining || 0,
      label: "Membership Credits",
      description: "Membership Sauna Credits",
      className: "membership",
    },
  ];

  const legacyRedLightOptions = [
    giftedPerTypeOption,
    sharedGiftedOption,
    wellnessWednesdayOption,
    {
      type: "drop-in",
      available: userProfile?.dropInRedLightCount?.remaining || 0,
      label: "Drop-in Credits",
      description: "Drop-in Red Light Credits",
      className: "drop-in",
    },
    {
      type: "package",
      available: userProfile?.redLightPackageSessionUsage?.remaining || 0,
      label: "Package Credits",
      description: "Package Red Light Credits",
      className: "package",
    },
    {
      type: "membership",
      available:
        userProfile?.redLightMembershipSessionUsage?.remaining || 0,
      label: "Membership Credits",
      description: "Membership Red Light Credits",
      className: "membership",
    },
  ];

  const creditOptions = isUnifiedShared
    ? unifiedSharedOptions
    : sessionTypeIsInfrared
      ? legacyInfraredOptions
      : legacyRedLightOptions;
  // Prefer the backend's list: it is what the booking will use, whatever
  // the customer's creditSystemType is.
  const CREDIT_SOURCE_LABELS = {
    "gifted-sauna": "Gifted Sauna Credits",
    "gifted-redlight": "Gifted Red Light Credits",
    "shared-gifted": "Gifted Credits",
    promotion: "Wellness Wednesday Credits",
    "drop-in": "Drop-in Credits",
    package: "Package Credits",
    membership: "Membership Credits",
    intro_offer: "Intro Offer",
  };
  const CREDIT_SOURCE_CLASSES = {
    gifted: "gifted",
    promotion: "drop-in",
    "drop-in": "drop-in",
    package: "package",
    membership: "membership",
  };
  const availableCreditOptions = creditSources
    ? creditSources.map((source) => ({
        type: source.creditTypeHint,
        field: source.field,
        available: source.available,
        label: CREDIT_SOURCE_LABELS[source.creditTypeHint] || source.description,
        description: source.description,
        className: CREDIT_SOURCE_CLASSES[source.type] || "",
      }))
    : creditOptions.filter((credit) => credit.available > 0);
  // One dropdown entry per credit type: a legacy and a shared bucket of the
  // same type share one hint; the booking uses them in priority order.
  const creditTypeChoices = availableCreditOptions.filter(
    (credit, index, all) =>
      all.findIndex((other) => other.type === credit.type) === index
  );
  // Staff can charge the saved card when the customer has no credit for
  // this session (and no corporate program or bypass covers it).
  const showSessionChargeOption =
    !isMassage &&
    !bypassSessionLimits &&
    !corporateProgramAppliesToSession &&
    creditSources !== null &&
    availableCreditOptions.length === 0;
  const showAddOnChargeOption =
    sessionType === "infrared" && !bypassSessionLimits && (addGuest || halotherapy);
  const sessionChargeDue = showSessionChargeOption && chargeSavedCardForSession;
  const addOnChargeDue = showAddOnChargeOption && chargeSavedCardForAddOns;
  // Something will be charged: the payment method choice is shown.
  const chargeDue = isMassage || sessionChargeDue || addOnChargeDue;
  const payWithReader = chargeDue && paymentMethod === "reader";
  const payWithText = payWithReader
    ? "the customer's card on the POS reader"
    : "the customer's saved card";
  const selectedReader =
    (cardReaders || []).find((reader) => reader.id === selectedReaderId) ||
    null;
  const isSimulatedReader = Boolean(
    selectedReader?.device_type?.startsWith("simulated")
  );

  // Load the card readers when staff first choose the reader.
  useEffect(() => {
    if (paymentMethod !== "reader" || cardReaders !== null) {
      return undefined;
    }
    let cancelled = false;
    staffAPI
      .listCardReaders()
      .then((response) => {
        if (cancelled) return;
        const readers = response.data?.readers || [];
        setCardReaders(readers);
        const online = readers.find((reader) => reader.status === "online");
        setSelectedReaderId((online || readers[0])?.id || "");
      })
      .catch(() => {
        if (cancelled) return;
        setCardReaders([]);
        setCardReadersError("Could not load the card readers.");
      });
    return () => {
      cancelled = true;
    };
  }, [paymentMethod, cardReaders]);

  // When the form closes: stop polling, and if the reader is still waiting
  // for a card, cancel that payment so the reader is free for the next one.
  useEffect(() => {
    const poll = readerPollRef.current;
    return () => {
      const waitingPaymentIntentId = poll.active ? poll.paymentIntentId : null;
      poll.active = false;
      if (waitingPaymentIntentId) {
        Promise.resolve()
          .then(() => staffAPI.cancelBookingTerminalPayment(waitingPaymentIntentId))
          .catch(() => {});
      }
    };
  }, []);

  const getCorporateBookingSummary = () => {
    if (!corporateProgramAppliesToSession) {
      return null;
    }

    if (corporateProgramStatus.nextBookingSourceType === "organization-sponsored") {
      return "Auto booking will use a sponsored corporate session.";
    }

    if (corporateProgramStatus.hasPersonalCreditsAvailable) {
      return "Personal credits will be used before the corporate rate.";
    }

    return `If no personal credits remain, auto booking will use the $${Number(
      corporateProgramStatus.corporateRate || 0
    ).toFixed(2)} corporate rate.`;
  };

  // Fetch user profile when component mounts
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!customerId) return;

        const response = await staffAPI.getCustomerById(customerId);
        setUserProfile(response.data);
        setIsAda(response.data.ada || false);
      } catch (err) {
        handleApiError(
          err,
          "StaffBookingForm.fetchUserProfile",
          setError,
          "Error fetching user profile"
        );
      }
    };
    fetchUserProfile();
  }, [customerId]);

  // Fetch available time slots when sessionType or selectedDay changes.
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        // Ensure selectedDay is not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDay < today) {
          setSelectedDay(today);
          return;
        }

        const year = selectedDay.getFullYear();
        const month = String(selectedDay.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDay.getDate()).padStart(2, "0");
        const dateString = `${year}-${month}-${day}`;

        const response = await bookingsAPI.getAvailableSessions(
          dateString,
          sessionType
        );

        // Backend already filters out past slots using Phoenix timezone
        // No additional filtering needed on frontend
        const slots = response.data.availableSlots.map(
          (slot) => new Date(slot)
        );

        setAvailableSlots(slots);

        // If slots exist, default the appointment date to the first available slot.
        if (slots.length > 0) {
          setAppointmentDate(slots[0]);
        } else {
          setAppointmentDate(null);
        }
      } catch (err) {
        handleApiError(
          err,
          "StaffBookingForm.fetchAvailability",
          setError,
          "Error fetching availability"
        );
      }
    };
    fetchAvailability();
  }, [sessionType, selectedDay]);

  // Filter available suites based on ADA status
  useEffect(() => {
    if (availableSuites.length > 0) {
      setFilteredSuites(availableSuites);
    } else {
      setFilteredSuites([]);
    }
  }, [availableSuites, isAda, appointmentDate]);

  // Fetch available suites when sessionType, selectedDay, or appointmentDate changes.
  useEffect(() => {
    const fetchSuites = async () => {
      try {
        const year = selectedDay.getFullYear();
        const month = String(selectedDay.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDay.getDate()).padStart(2, "0");
        const dateString = `${year}-${month}-${day}`;
        if (!appointmentDate) return;

        // Format appointmentDate as a Phoenix local time string (HH:mm)
        const phoenixTime = moment(appointmentDate)
          .tz("America/Phoenix")
          .format("HH:mm");

        // Massage does not use the suites API (no infrared / red-light suite
        // selection). The backend assigns the massage room automatically.
        if (sessionType === "massage_60" || sessionType === "massage_90") {
          setAvailableSuites([]);
          setSelectedSuite("");
          return;
        }

        let response;
        if (sessionType === "infrared") {
          response = await suitesAPI.getSaunaAvailability(
            dateString,
            phoenixTime,
            isAda // Pass isAda flag
          );
        } else {
          response = await suitesAPI.getRedLightAvailability(
            dateString,
            phoenixTime,
            isAda // Pass isAda flag
          );
        }

        // Sort suites by suiteNumber in ascending order
        const sortedSuites = (response.data.suites || []).sort(
          (a, b) => a.suiteNumber - b.suiteNumber
        );
        setAvailableSuites(sortedSuites);
        setSelectedSuite("");
      } catch (err) {
        handleApiError(
          err,
          "StaffBookingForm.fetchSuites",
          () => {
            console.error("Error fetching available suites:", err);
            setAvailableSuites([]);
          },
          "Error fetching available suites"
        );
      }
    };
    fetchSuites();
  }, [sessionType, selectedDay, appointmentDate, isAda]);

  useEffect(() => {
    if (
      creditTypeToUse !== "auto" &&
      !availableCreditOptions.some((credit) => credit.type === creditTypeToUse)
    ) {
      setCreditTypeToUse("auto");
    }
  }, [availableCreditOptions, creditTypeToUse]);

  // The booking details a reader payment is for. A paid payment can be
  // reused only for the same details.
  const readerPaymentKey = (bookingData) =>
    JSON.stringify({
      userId: bookingData.userId,
      sessionType: bookingData.sessionType,
      appointmentDate: bookingData.appointmentDate,
      chargeSession: isMassage ? false : sessionChargeDue,
      addOnTypes: isMassage || !addOnChargeDue
        ? []
        : [addGuest && "guest", halotherapy && "halotherapy"].filter(Boolean),
    });

  const waitForReaderPayment = async (paymentIntentId) => {
    const poll = readerPollRef.current;
    poll.active = true;
    poll.paymentIntentId = paymentIntentId;
    const deadline = Date.now() + 3 * 60 * 1000;
    while (poll.active && poll.paymentIntentId === paymentIntentId) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 2000));
      if (!poll.active || poll.paymentIntentId !== paymentIntentId) {
        return { status: "stopped" };
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await staffAPI.getBookingTerminalPayment(
          paymentIntentId
        );
        if (data.status !== "pending") {
          poll.active = false;
          return data;
        }
      } catch (pollError) {
        // A short network problem: keep waiting until the deadline.
      }
      if (Date.now() > deadline) {
        poll.active = false;
        return { status: "timeout" };
      }
    }
    return { status: "stopped" };
  };

  // Takes payment on the reader. Returns the paid PaymentIntent ID, or null
  // when the payment did not complete (the form shows why).
  const payOnReader = async (bookingData) => {
    const key = readerPaymentKey(bookingData);
    if (unusedReaderPayment) {
      if (unusedReaderPayment.key === key) {
        return unusedReaderPayment.paymentIntentId;
      }
      setError(
        `The customer already paid ${formatCents(
          unusedReaderPayment.amount
        )} on the reader for different booking details. Refund that payment first, or change the booking back.`
      );
      return null;
    }
    if (!selectedReaderId) {
      setError("Choose a card reader.");
      return null;
    }

    // Run the booking checks first, so the customer does not pay for a
    // booking that will be refused.
    const check = await staffAPI.bookSessionForUser({
      ...bookingData,
      validateOnly: true,
    });
    if (!isMassage && sessionChargeDue && check.data?.hasCredit) {
      setError(
        "The customer has a credit for this session now. Clear the drop-in charge and book with the credit."
      );
      return null;
    }

    const { data: started } = await staffAPI.startBookingTerminalPayment({
      userId: bookingData.userId,
      sessionType: bookingData.sessionType,
      appointmentDate: bookingData.appointmentDate,
      readerId: selectedReaderId,
      requestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      chargeSession: isMassage ? false : sessionChargeDue,
      addOnTypes: JSON.parse(key).addOnTypes,
      ...(isMassage ? { overrideMassageTurnover } : {}),
    });
    setReaderPayment({
      paymentIntentId: started.paymentIntentId,
      amount: started.amount,
      status: "waiting",
      key,
    });

    const result = await waitForReaderPayment(started.paymentIntentId);
    if (result.status === "succeeded") {
      setReaderPayment({
        paymentIntentId: started.paymentIntentId,
        amount: started.amount,
        status: "paid",
      });
      setUnusedReaderPayment({
        paymentIntentId: started.paymentIntentId,
        amount: started.amount,
        key,
      });
      return started.paymentIntentId;
    }
    if (result.status === "failed") {
      setReaderPayment(null);
      // Close the declined payment; a new attempt creates a new one.
      Promise.resolve()
        .then(() => staffAPI.cancelBookingTerminalPayment(started.paymentIntentId))
        .catch(() => {});
      const reason = String(result.message || "declined").replace(/[.\s]+$/, "");
      setError(
        `The card reader payment failed: ${reason}. Try again or use the saved card.`
      );
    } else if (result.status === "canceled") {
      setReaderPayment(null);
      setError(result.message || "The card reader payment was cancelled.");
    } else if (result.status === "timeout") {
      // One state only: the box says what to do (no separate error).
      setReaderPayment({
        paymentIntentId: started.paymentIntentId,
        amount: started.amount,
        status: "timeout",
        key,
        note: "The reader has not received a card after 3 minutes. Cancel the payment, then try again.",
      });
    }
    return null;
  };

  const serverMessage = (err, fallback) =>
    err?.response?.data?.message || fallback;

  // One check of the payment, used when polling has stopped (after the
  // 3-minute timeout). Shows exactly one state: paid, failed, cancelled, or
  // still waiting.
  const checkReaderPaymentOnce = async (payment, noteIfWaiting) => {
    try {
      const { data } = await staffAPI.getBookingTerminalPayment(payment.paymentIntentId);
      if (data.status === "succeeded") {
        setReaderPayment(null);
        setUnusedReaderPayment({
          paymentIntentId: payment.paymentIntentId,
          amount: payment.amount,
          key: payment.key,
        });
        setError("");
        return;
      }
      if (data.status === "failed" || data.status === "canceled") {
        setReaderPayment(null);
        setError(
          data.message ||
            (data.status === "failed"
              ? "The card reader payment failed."
              : "The card reader payment was cancelled.")
        );
        return;
      }
      setReaderPayment({ ...payment, note: noteIfWaiting });
    } catch (checkError) {
      setReaderPayment({
        ...payment,
        note: `${noteIfWaiting} The payment status could not be read; try again in a moment.`,
      });
    }
  };

  const handleCancelReaderPayment = async () => {
    if (!readerPayment) return;
    const payment = readerPayment;
    const poll = readerPollRef.current;
    const polling = poll.active && poll.paymentIntentId === payment.paymentIntentId;
    try {
      await staffAPI.cancelBookingTerminalPayment(payment.paymentIntentId);
      poll.active = false;
      setReaderPayment(null);
      setLoading(false);
      setError("The card reader payment was cancelled.");
    } catch (err) {
      if (err?.response?.data?.code === "STAFF_TERMINAL_ALREADY_PAID") {
        if (polling) {
          // The card was accepted just before the cancel; the running
          // check sees the payment and finishes the booking.
          setReaderPayment({ ...payment, note: "The customer had already paid. Finishing the booking..." });
          return;
        }
        setReaderPayment(null);
        setUnusedReaderPayment({
          paymentIntentId: payment.paymentIntentId,
          amount: payment.amount,
          key: payment.key,
        });
        setError("");
        return;
      }
      const reason = serverMessage(err, "The payment could not be cancelled.");
      if (polling) {
        setReaderPayment({ ...payment, note: `${reason} Still waiting for the reader.` });
        return;
      }
      setLoading(false);
      await checkReaderPaymentOnce(payment, reason);
    }
  };

  const handleSimulateTap = async () => {
    if (!readerPayment) return;
    const payment = readerPayment;
    try {
      await staffAPI.simulateBookingTerminalTap(payment.paymentIntentId);
    } catch (err) {
      // No separate error: the running status check settles the payment
      // (for example "no longer on the reader", which cancels it).
      setReaderPayment({
        ...payment,
        note: `The simulated tap did not go through (${serverMessage(
          err,
          "unknown reason"
        )}). Checking the payment...`,
      });
    }
  };

  const handleRefundUnusedReaderPayment = async () => {
    if (!unusedReaderPayment) return;
    setLoading(true);
    try {
      await staffAPI.refundBookingTerminalPayment(
        unusedReaderPayment.paymentIntentId
      );
      setMessage(
        `Refunded ${formatCents(unusedReaderPayment.amount)} to the customer's card.`
      );
      setError("");
      setUnusedReaderPayment(null);
      setReaderPayment(null);
    } catch (err) {
      handleApiError(
        err,
        "StaffBookingForm.handleRefundUnusedReaderPayment",
        setError,
        "Could not refund the card reader payment."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setReaderBusy(false);
    setLoading(true);

    // Validate required fields.
    if (!customerId) {
      setError("No customer selected.");
      setLoading(false);
      return;
    }

    if (!appointmentDate) {
      setError("Please select a time slot.");
      setLoading(false);
      return;
    }

    // Suite selection is required for infrared / redlight, but NOT for massage
    // (massage does not use suites — auto-assigned by BE).
    if (!isMassage && !selectedSuite) {
      setError("Please select an available suite.");
      setLoading(false);
      return;
    }

    if (new Date(appointmentDate) < new Date()) {
      setError(
        "The selected time is in the past. Please choose a future time slot."
      );
      setLoading(false);
      return;
    }

    try {
      // Prepare booking data.
      // Massage bookings send suite: null (the backend assigns the room) and
      // creditTypeToUse: "auto" — massages are charged to the saved card,
      // not taken from credits. Infrared / redlight payload shape is
      // byte-preserved (Scenario 3).
      const bookingData = {
        userId: customerId,
        sessionType,
        appointmentDate: moment(appointmentDate)
          .tz("America/Phoenix")
          .toISOString(),
        suite: isMassage ? null : Number(selectedSuite),
        addGuest,
        aromatherapy,
        halotherapy,
        bypassSessionLimits,
        creditTypeToUse: isMassage
          ? "auto"
          : bypassSessionLimits
            ? "auto"
            : creditTypeToUse,
        ...(isMassage ? { overrideMassageTurnover } : {}),
        ...(!payWithReader && showSessionChargeOption && chargeSavedCardForSession
          ? { chargeSavedCardForSession: true }
          : {}),
        ...(!payWithReader && showAddOnChargeOption
          ? { chargeSavedCardForAddOns }
          : {}),
      };

      if (payWithReader) {
        const paymentIntentId = await payOnReader(bookingData);
        if (!paymentIntentId) {
          setLoading(false);
          return;
        }
        bookingData.terminalPaymentIntentId = paymentIntentId;
      }

      // Call the staff API to book the session for the user
      const response = await staffAPI.bookSessionForUser(bookingData);
      if (bookingData.terminalPaymentIntentId) {
        setUnusedReaderPayment(null);
        setReaderPayment(null);
      }

      setMessage(
        response.data.message || "Session booked successfully for user!"
      );
      setLoading(false);

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess(response.data.booking);
      }
    } catch (err) {
      setLoading(false);
      // The server used or refunded the reader payment for these errors,
      // so it can no longer be reused or refunded from the form.
      if (
        [
          "STAFF_TERMINAL_MISMATCH",
          "STAFF_TERMINAL_NOT_NEEDED",
          "STAFF_TERMINAL_ALREADY_USED",
          "STAFF_BOOKING_NO_CREDIT",
        ].includes(err?.response?.data?.code)
      ) {
        setUnusedReaderPayment(null);
        setReaderPayment(null);
      }
      setReaderBusy(err?.response?.data?.code === "STAFF_TERMINAL_READER_BUSY");
      handleApiError(
        err,
        "StaffBookingForm.handleSubmit",
        setError,
        "Error booking session. Please try again."
      );
    }
  };

  // "Clear reader": cancels the action waiting on the reader, and the
  // payment on it only if it is unpaid (the server never cancels a paid
  // one). Then staff press Book again.
  const handleClearReader = async () => {
    if (
      !window.confirm(
        "Another payment is waiting on the reader. Clear it? The customer will not be charged for it."
      )
    ) {
      return;
    }
    setClearingReader(true);
    try {
      const { data } = await staffAPI.clearCardReader(selectedReaderId);
      setReaderBusy(false);
      setError("");
      setMessage(`${data?.message || "The card reader was cleared."} Press Book again.`);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Could not clear the card reader. Try again."
      );
    } finally {
      setClearingReader(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="staff-booking-form">
      <h2>
        Book a Session for{" "}
        {userProfile
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : "Customer"}
      </h2>

      <div className="form-group">
        <label htmlFor="sessionType">Session Type:</label>
        <select
          id="sessionType"
          value={sessionType}
          onChange={(e) => {
            setSessionType(e.target.value);
            setAvailableSlots([]);
            setAvailableSuites([]);
            setAppointmentDate(null);
            setSelectedSuite("");
          }}
        >
          <option key="infrared" value="infrared">
            50 Min Infrared Sauna Session
          </option>
          <option key="redlight" value="redlight">
            20 Min Red Light Bed Session
          </option>
          <option key="massage_60" value="massage_60">
            60 Min Massage
          </option>
          <option key="massage_90" value="massage_90">
            90 Min Massage
          </option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="selectedDay">Select a Day:</label>
        <DatePicker
          id="selectedDay"
          selected={selectedDay}
          onChange={(date) => {
            setSelectedDay(date);
            setAppointmentDate(null);
            setSelectedSuite("");
          }}
          dateFormat="MMMM d, yyyy"
          minDate={new Date()}
          placeholderText="Select a date - Available from today"
        />
      </div>

      <div className="available-slots">
        <h3>
          Available Time Slots for {selectedDay.toLocaleDateString()}{" "}
          <span className="slot-count">
            ({availableSlots.length} available)
          </span>
        </h3>
        <div className="slots-container">
          {availableSlots.length > 0 ? (
            availableSlots.map((slot, index) => (
              <button
                type="button"
                key={index}
                className={`slot-btn ${
                  appointmentDate &&
                  slot.getTime() === appointmentDate.getTime()
                    ? "selected"
                    : ""
                }`}
                onClick={() => setAppointmentDate(slot)}
              >
                {slot.toLocaleTimeString("en-US", {
                  timeZone: "America/Phoenix",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </button>
            ))
          ) : (
            <p>No available slots for this day.</p>
          )}
        </div>
      </div>

      {isMassage ? null : filteredSuites.length > 0 ? (
        <div className="form-group">
          <label htmlFor="suiteSelection">
            Select{" "}
            {sessionType === "infrared" ? "Sauna Suite" : "Red Light Bed Suite"}
            :
          </label>
          <select
            id="suiteSelection"
            value={selectedSuite}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedSuite(value);
            }}
            required
          >
            <option key="default-suite" value="">
              -- Select a Suite --
            </option>

            {filteredSuites.map((suite) => (
              <option
                key={suite.id || suite.suiteNumber || suite.name}
                value={suite.id || suite.suiteNumber}
              >
                {suite.name}
              </option>
            ))}
          </select>
          {isAda &&
            filteredSuites.length > 0 &&
            filteredSuites.length < availableSuites.length && (
              <div className="ada-note">
                <small>
                  As an ADA member, this customer is shown handicap-accessible
                  suites first.
                </small>
              </div>
            )}
        </div>
      ) : (
        <div className="form-group">
          <p>No available suites for this session type on this day.</p>
        </div>
      )}

      {appointmentDate && (
        <p>
          Selected Time:{" "}
          {appointmentDate.toLocaleTimeString("en-US", {
            timeZone: "America/Phoenix",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </p>
      )}

      {sessionType === "infrared" && (
        <>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={addGuest}
                onChange={(e) => setAddGuest(e.target.checked)}
              />
              Add a Guest
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={aromatherapy}
                onChange={(e) => setAromatherapy(e.target.checked)}
              />
              Add Aromatherapy
            </label>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={halotherapy}
                onChange={(e) => setHalotherapy(e.target.checked)}
              />
              Add Halo Therapy
            </label>
          </div>
          {showAddOnChargeOption && (
            <div
              className="form-group checkbox-group staff-charge-option"
              data-testid="staff-addon-charge"
            >
              <label>
                <input
                  type="checkbox"
                  checked={chargeSavedCardForAddOns}
                  onChange={(e) => setChargeSavedCardForAddOns(e.target.checked)}
                />
                <span className="staff-charge-option-text">
                Charge {payWithText} for{" "}
                {[
                  addGuest &&
                    `the guest${
                      dayPricing?.addOns?.guest
                        ? ` (${formatCents(dayPricing.addOns.guest.totalCents)}${
                            dayPricing.isWellnessWednesday
                              ? ", Wellness Wednesday price"
                              : ""
                          })`
                        : ""
                    }`,
                  halotherapy &&
                    `halotherapy${
                      dayPricing?.addOns?.halotherapy
                        ? ` (${formatCents(dayPricing.addOns.halotherapy.totalCents)})`
                        : ""
                    }`,
                ]
                  .filter(Boolean)
                  .join(" and ")}
                , tax included.
                </span>
              </label>
            </div>
          )}
        </>
      )}

      {corporateProgramAppliesToSession && (
        <div className="corporate-booking-panel">
          <div className="corporate-booking-header">
            Corporate Program: {corporateProgramStatus.organizationCode} •{" "}
            {corporateProgramStatus.programName}
          </div>
          <div className="corporate-booking-body">
            <p>{corporateProgramStatus.summaryMessage}</p>
            <p>
              <strong>Sponsored used this month:</strong>{" "}
              {corporateProgramStatus.employeeSponsoredUsed || 0} /{" "}
              {corporateProgramStatus.sponsoredSessionsPerEmployeePerMonth || 0}
            </p>
            <p>
              <strong>Corporate-rate sessions this month:</strong>{" "}
              {corporateProgramStatus.employeeCorporateRateUsed || 0}
            </p>
            <p>
              <strong>Booking behavior:</strong> {getCorporateBookingSummary()}
            </p>
          </div>
        </div>
      )}

      {/* Massage: charged to the customer's saved card. Credits and bypass
          do not apply. */}
      {isMassage && (
        <div className="form-group" data-testid="staff-massage-payment">
          <label>Payment:</label>
          <p className="staff-massage-payment-note">
            {payWithReader
              ? `The customer pays ${
                  massagePricing
                    ? formatCents(massagePricing.totalCents)
                    : "the massage price plus tax"
                } on the card reader when you book.`
              : massagePricing
              ? `The customer's saved card will be charged $${(
                  massagePricing.totalCents / 100
                ).toFixed(2)} ($${(massagePricing.amountCents / 100).toFixed(
                  2
                )} + $${(massagePricing.taxCents / 100).toFixed(
                  2
                )} AZ/Pima tax) when you book.`
              : "The customer's saved card will be charged the massage price plus tax when you book."}{" "}
            The therapist and room are assigned automatically.
          </p>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={overrideMassageTurnover}
              onChange={(e) => setOverrideMassageTurnover(e.target.checked)}
            />
            Override the turnover time between massages (the room must be
            free)
          </label>
        </div>
      )}

      {/* Credits + bypass are hidden for massage — massages are charged to
          the saved card, not taken from credits. Deployed infrared /
          redlight rendering is unchanged (Scenario 3 regression pin). */}
      {!isMassage && (
        <>
      {/* Credit Type Information */}
      <div className="form-group">
        <label>Available Credits:</label>
        <div className="credit-type-info">
          {availableCreditOptions.map((credit) => (
            <div
              key={credit.field || credit.type}
              className={`credit-available ${credit.className}`}
            >
              <span className="credit-type">{credit.description}:</span>{" "}
              {credit.available} available
            </div>
          ))}

          {availableCreditOptions.length === 0 && (
            <div className="credit-available none">
              {corporateProgramAppliesToSession
                ? `No personal ${
                    isUnifiedShared
                      ? "shared"
                      : sessionType === "infrared"
                        ? "sauna"
                        : "red light"
                  } credits available. ${getCorporateBookingSummary()}`
                : `No ${
                    sessionType === "infrared" ? "sauna" : "red light"
                  } credits available for this day`}
            </div>
          )}
        </div>

        {/* Below the "No credits" message, which explains why. */}
      {showSessionChargeOption && (
        <div
          className="form-group checkbox-group staff-charge-option"
          data-testid="staff-session-charge"
        >
          <label>
            <input
              type="checkbox"
              checked={chargeSavedCardForSession}
              onChange={(e) =>
                setChargeSavedCardForSession(e.target.checked)
              }
            />
            <span className="staff-charge-option-text">
              Charge {payWithText} for a{" "}
              {dayPricing?.isWellnessWednesday
                ? "Wellness Wednesday drop-in"
                : "drop-in"}
              {dayPricing?.dropIn
                ? `: ${formatCents(dayPricing.dropIn.totalCents)} (${formatCents(
                    dayPricing.dropIn.amountCents
                  )} + ${formatCents(dayPricing.dropIn.taxCents)} AZ/Pima tax)`
                : ""}
              . The customer gets the drop-in credit and this booking uses
              it.
            </span>
          </label>
        </div>
      )}
      </div>

      {/* Credit Type Selection: only when the customer has a credit. */}
      {availableCreditOptions.length > 0 && (
        <div className="form-group">
          <label>Credit Type to Use:</label>
          <select
            value={creditTypeToUse}
            onChange={(e) => setCreditTypeToUse(e.target.value)}
            disabled={bypassSessionLimits}
          >
            <option value="auto">Auto (Default Priority)</option>
            {creditTypeChoices.map((credit) => (
              <option key={credit.type} value={credit.type}>
                {credit.label}
              </option>
            ))}
          </select>

          {creditTypeToUse === "auto" && (
            <div className="credit-type-info">
              <small>
                Priority order:{" "}
                {corporateProgramAppliesToSession &&
                corporateProgramStatus.nextBookingSourceType ===
                  "organization-sponsored"
                  ? "Corporate Sponsored → "
                  : ""}
                Gifted → Drop-in → Package → Membership
                {corporateProgramAppliesToSession &&
                corporateProgramStatus.nextBookingSourceType ===
                  "organization-corporate-rate" &&
                !corporateProgramStatus.hasPersonalCreditsAvailable
                  ? ` → $${Number(
                      corporateProgramStatus.corporateRate || 0
                    ).toFixed(2)} Corporate Rate`
                  : ""}
              </small>
            </div>
          )}
        </div>
      )}

      <div className="form-group checkbox-group staff-options">
        <label>
          <input
            type="checkbox"
            checked={bypassSessionLimits}
            onChange={(e) => {
              setBypassSessionLimits(e.target.checked);
              if (e.target.checked) {
                setCreditTypeToUse("auto");
              }
            }}
          />
          Bypass Session Limits
          <span
            className="tooltip"
            data-tooltip="Enable this option to book a session even if the user has no remaining sessions. This will not deduct from their session count."
          ></span>
        </label>
      </div>
        </>
      )}

      {chargeDue && (
        <div className="form-group staff-payment-method" data-testid="staff-payment-method">
          <label>Pay with:</label>
          <div className="staff-payment-method-options">
            <label className="checkbox-label">
              <input
                type="radio"
                name="staff-payment-method"
                value="saved_card"
                checked={paymentMethod === "saved_card"}
                disabled={Boolean(readerPayment || unusedReaderPayment)}
                onChange={() => setPaymentMethod("saved_card")}
              />
              Saved card
            </label>
            <label className="checkbox-label">
              <input
                type="radio"
                name="staff-payment-method"
                value="reader"
                checked={paymentMethod === "reader"}
                onChange={() => setPaymentMethod("reader")}
              />
              POS card reader
            </label>
          </div>
          {payWithReader && cardReaders === null && (
            <p className="staff-payment-note">Loading card readers...</p>
          )}
          {payWithReader && cardReaders !== null && cardReaders.length === 0 && (
            <p className="staff-payment-note error">
              {cardReadersError || "No card readers are registered."}
            </p>
          )}
          {payWithReader && cardReaders && cardReaders.length > 0 && (
            <select
              aria-label="Card reader"
              value={selectedReaderId}
              disabled={Boolean(readerPayment)}
              onChange={(e) => setSelectedReaderId(e.target.value)}
            >
              {cardReaders.map((reader) => (
                <option key={reader.id} value={reader.id}>
                  {reader.label || reader.id}
                  {reader.status ? ` (${reader.status})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {readerPayment && readerPayment.status !== "paid" && (
        <div className="staff-reader-status" data-testid="staff-reader-status" role="status">
          <p>
            {readerPayment.status === "waiting"
              ? `Waiting for the customer to pay ${formatCents(
                  readerPayment.amount
                )} on the card reader.`
              : `The reader is still waiting for ${formatCents(
                  readerPayment.amount
                )}.`}
          </p>
          {readerPayment.note && (
            <p className="staff-reader-note" data-testid="staff-reader-note">
              {readerPayment.note}
            </p>
          )}
          <div className="staff-reader-actions">
            {isSimulatedReader && readerPayment.status === "waiting" && (
              <button
                type="button"
                className="action-btn action-btn-secondary"
                onClick={handleSimulateTap}
              >
                Simulate card tap (test mode)
              </button>
            )}
            <button
              type="button"
              className="action-btn action-btn-secondary"
              onClick={handleCancelReaderPayment}
            >
              Cancel reader payment
            </button>
          </div>
        </div>
      )}

      {unusedReaderPayment && !loading && (
        <div className="staff-reader-status" data-testid="staff-reader-unused" role="status">
          <p>
            The customer paid {formatCents(unusedReaderPayment.amount)} on the
            reader, but the booking was not made. Fix the problem and press
            Book to use this payment, or refund it.
          </p>
          <div className="staff-reader-actions">
            <button
              type="button"
              className="action-btn action-btn-secondary"
              onClick={handleRefundUnusedReaderPayment}
            >
              Refund {formatCents(unusedReaderPayment.amount)}
            </button>
          </div>
        </div>
      )}

      {/* Result message, next to the Book button so staff see it. */}
      {(message || error) && (
        <div
          ref={notificationRef}
          role={error ? "alert" : "status"}
          className={`notification-container ${message ? "success" : "error"}`}
        >
          <div className="notification-icon">{message ? "✓" : "⚠"}</div>
          <div className="notification-message">{message || error}</div>
        </div>
      )}
      {readerBusy && error && payWithReader && selectedReaderId && (
        <div className="staff-reader-actions" data-testid="staff-reader-busy">
          <button
            type="button"
            className="action-btn action-btn-secondary"
            onClick={handleClearReader}
            disabled={clearingReader}
          >
            {clearingReader ? "Clearing..." : "Clear reader"}
          </button>
        </div>
      )}

      <div className="form-actions">
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className="action-btn action-btn-primary"
        >
          {loading
            ? readerPayment?.status === "waiting"
              ? "Waiting for card..."
              : "Booking..."
            : "Book"}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="action-btn action-btn-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default StaffBookingForm;
